import React, {
  useState,
  useEffect,
  useCallback,
  useMemo,
  useRef,
} from "react";
import { useSearchParams } from "react-router-dom";
import WidgetsOutlinedIcon from "@mui/icons-material/WidgetsOutlined";
import { CircularProgress } from "@mui/material";
import Autocomplete, { createFilterOptions } from "@mui/material/Autocomplete";
import TextField from "@mui/material/TextField";
import { ReportServices } from "../services/ReportServices";
import { InterUserMangementServices } from "../services/InternalUserMangementServices";
import { handleApiErrorResponses } from "../utils/ApisErrorHandling";
import HospitalCustomPaginaion from "../utils/HospitalPaginationUtil";
import calenderIcon from "../assets/calenderIcon.svg";
import EditIcon from "../assets/editIcon.svg";
import crossIcon from "../assets/crossIcon.svg";
import { updateCCStageTime } from "../services/InternalUserMangementServices";
import { Switch } from "@mui/material";

function toIsoDateInput(isoOrNull) {
  if (isoOrNull == null || isoOrNull === "") return "";
  const d = new Date(isoOrNull);
  if (Number.isNaN(d.getTime())) return "";
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

const CC_DOCTOR_LIST_LIMIT = 500;
/** Page size for GET get-cc-stage-time (matches API `count`). */
const CC_STAGE_TIME_PAGE_SIZE = 10;

/** Shared MUI field styling so doctor + stage filters look identical */
const FILTER_FIELD_SX = {
  "& .MuiOutlinedInput-root": {
    borderRadius: "8px",
    fontFamily: "inherit",
    fontSize: "0.875rem",
    minHeight: "40px",
    backgroundColor: "#fff",
  },
};

const filterStageOptionsCore = createFilterOptions({
  stringify: (option) => option.label,
  ignoreCase: true,
  matchFrom: "any",
});

/** Trim typed input so leading/trailing whitespace does not break MUI option matching. */
function filterStageOptions(options, state) {
  return filterStageOptionsCore(options, {
    ...state,
    inputValue: String(state.inputValue ?? "").trim(),
  });
}

/** Time limit field: digits only (letters/symbols stripped; paste is sanitized). */
function digitsOnlyTimeLimit(value) {
  return String(value ?? "").replace(/\D/g, "");
}

function doctorDisplayName(u) {
  if (!u) return "—";
  const fn = u.fullName != null ? String(u.fullName).trim() : "";
  if (fn) return fn;
  const un = u.userName != null ? String(u.userName).trim() : "";
  if (un) return un;
  const em = u.email != null ? String(u.email).trim() : "";
  return em || "—";
}

function normalizeStageList(res) {
  const raw =
    res?.data?.commandCenterStageList ?? res?.data?.data ?? res?.data ?? [];
  if (!Array.isArray(raw)) return [];
  return raw
    .map((s) => ({
      id: Number(s.id),
      label:
        s.commandCenterStageStatus != null
          ? String(s.commandCenterStageStatus)
          : "",
    }))
    .filter((s) => Number.isFinite(s.id) && s.label);
}

/** Stable id string for lookup (GET may use userId, doctorId, or snake_case). */
function rowDoctorUserId(row) {
  const v = row?.userId ?? row?.doctorId ?? row?.user_id ?? row?.doctor_id;
  if (v == null || v === "") return "";
  return String(v).trim();
}

/** Name fields that may appear on a cc-stage-time row from the API. */
function nameFromRowFields(row) {
  const raw =
    row?.fullName ??
    row?.userName ??
    row?.doctorName ??
    row?.name ??
    row?.email ??
    row?.full_name ??
    row?.user_name;
  if (raw != null && String(raw).trim() !== "") return String(raw).trim();
  return "";
}

/** Prefer name on the row; else map from userId → label (doctor list + row-derived cache). */
function rowDoctorDisplayName(row, nameByUserId) {
  const fromRow = nameFromRowFields(row);
  if (fromRow) return fromRow;
  const uid = rowDoctorUserId(row);
  if (uid && nameByUserId[uid]) return nameByUserId[uid];
  return uid ? `User #${uid}` : "—";
}

/**
 * @param {string|null|undefined} doctorId — From Doctor Performance (embedded). When omitted, `?doctorId=` is used on the standalone route.
 * Standalone without `?doctorId=` still loads the full list from GET (no userId filter).
 */
export default function DutCaseStageUpdate({ doctorId: doctorIdProp }) {
  const [searchParams, setSearchParams] = useSearchParams();
  const isEmbedded = doctorIdProp !== undefined;
  const effectiveDoctorId = useMemo(() => {
    if (doctorIdProp !== undefined) {
      return doctorIdProp != null && String(doctorIdProp).trim() !== ""
        ? doctorIdProp
        : null;
    }
    const q = searchParams.get("doctorId");
    return q != null && String(q).trim() !== "" ? q : null;
  }, [doctorIdProp, searchParams]);

  const doctorIdFromUrl = searchParams.get("doctorId");

  const [rows, setRows] = useState([]);
  const [listPage, setListPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [stageOptions, setStageOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingRow, setEditingRow] = useState(null);

  const [formDoctorId, setFormDoctorId] = useState("");
  const [doctorOptions, setDoctorOptions] = useState([]);
  /** Accumulated id → label from every doctor fetch so table can resolve names after search narrows `doctorOptions`. */
  const [doctorLabelById, setDoctorLabelById] = useState({});
  const [doctorsLoading, setDoctorsLoading] = useState(false);
  /** Standalone: filter list by doctor; empty = all doctors. */
  const [filterUserId, setFilterUserId] = useState("");
  /** Filter by stage; empty = all stages. */
  const [filterStageStatusId, setFilterStageStatusId] = useState("");
  const [formStageStatusId, setFormStageStatusId] = useState("");
  const [formSlotStart, setFormSlotStart] = useState("");
  const [formSlotEnd, setFormSlotEnd] = useState("");
  const [formTimeLimit, setFormTimeLimit] = useState("");

  const loadStages = useCallback(async () => {
    try {
      const res = await ReportServices.getDefaultCaseStages({
        roles: JSON.stringify(["CCCoordinator"]),
      });
      setStageOptions(normalizeStageList(res));
    } catch (e) {
      handleApiErrorResponses(e);
      setStageOptions([]);
    }
  }, []);

  const loadDoctors = useCallback(async (searchKey = "") => {
    setDoctorsLoading(true);
    try {
      const res = await InterUserMangementServices.getDutTeamUsers({
        limit: CC_DOCTOR_LIST_LIMIT,
        page: 1,
        searchKey,
      });
      const teamUsers = Array.isArray(res?.data?.teamUsers)
        ? res.data.teamUsers
        : [];
      const list = teamUsers
        .filter((u) => u?.id)
        .map((u) => ({
          id: String(u.id),
          label: doctorDisplayName(u),
        }));
      setDoctorOptions(list);
      setDoctorLabelById((prev) => {
        const next = { ...prev };
        for (const d of list) {
          next[d.id] = d.label;
        }
        return next;
      });
    } catch (e) {
      handleApiErrorResponses(e);
      setDoctorOptions([]);
    } finally {
      setDoctorsLoading(false);
    }
  }, []);

  const doctorSearchDebounceRef = useRef(null);
  const modalDoctorSearchDebounceRef = useRef(null);

  const removeDoctorIdFromUrl = useCallback(() => {
    if (isEmbedded) return;
    const next = new URLSearchParams(searchParams);
    next.delete("doctorId");
    setSearchParams(next, { replace: true });
  }, [isEmbedded, searchParams, setSearchParams]);

  const handleDoctorFilterInputChange = useCallback(
    (_event, inputValue, reason) => {
      if (reason === "clear") {
        setFilterUserId("");
        removeDoctorIdFromUrl();
        loadDoctors("");
        return;
      }
      if (reason !== "input") return;
      if (doctorSearchDebounceRef.current) {
        clearTimeout(doctorSearchDebounceRef.current);
      }
      doctorSearchDebounceRef.current = setTimeout(() => {
        loadDoctors(String(inputValue ?? "").trim());
        doctorSearchDebounceRef.current = null;
      }, 350);
    },
    [loadDoctors, removeDoctorIdFromUrl],
  );

  const handleModalDoctorInputChange = useCallback(
    (_event, inputValue, reason) => {
      if (reason === "clear") {
        setFormDoctorId("");
        loadDoctors("");
        return;
      }
      if (reason !== "input") return;
      if (modalDoctorSearchDebounceRef.current) {
        clearTimeout(modalDoctorSearchDebounceRef.current);
      }
      modalDoctorSearchDebounceRef.current = setTimeout(() => {
        loadDoctors(String(inputValue ?? "").trim());
        modalDoctorSearchDebounceRef.current = null;
      }, 350);
    },
    [loadDoctors],
  );

  /** userId/doctorId → display name: accumulated CC doctor fetches plus names on each table row. */
  const tableDoctorNameByUserId = useMemo(() => {
    const m = { ...doctorLabelById };
    for (const row of rows) {
      const uid = rowDoctorUserId(row);
      if (!uid) continue;
      const n = nameFromRowFields(row);
      if (n && !m[uid]) m[uid] = n;
    }
    return m;
  }, [doctorLabelById, rows]);

  const doctorFilterValue = useMemo(() => {
    if (!filterUserId) return null;
    const found = doctorOptions.find((d) => d.id === filterUserId);
    if (found) return found;
    return {
      id: filterUserId,
      label: doctorLabelById[filterUserId] || `User #${filterUserId}`,
    };
  }, [filterUserId, doctorOptions, doctorLabelById]);

  const doctorAutocompleteOptions = useMemo(() => {
    if (!filterUserId) return doctorOptions;
    const found = doctorOptions.find((d) => d.id === filterUserId);
    if (found) return doctorOptions;
    return doctorOptions.concat([
      {
        id: filterUserId,
        label: doctorLabelById[filterUserId] || `User #${filterUserId}`,
      },
    ]);
  }, [doctorOptions, filterUserId, doctorLabelById]);

  /** Add/Edit modal — value object for Autocomplete (searchable doctor list). */
  const modalDoctorValue = useMemo(() => {
    if (!formDoctorId) return null;
    const found = doctorOptions.find((d) => d.id === formDoctorId);
    if (found) return found;
    return {
      id: formDoctorId,
      label: doctorLabelById[formDoctorId] || `User #${formDoctorId}`,
    };
  }, [formDoctorId, doctorOptions, doctorLabelById]);

  const modalDoctorAutocompleteOptions = useMemo(() => {
    if (!formDoctorId) return doctorOptions;
    const found = doctorOptions.find((d) => d.id === formDoctorId);
    if (found) return doctorOptions;
    return doctorOptions.concat([
      {
        id: formDoctorId,
        label: doctorLabelById[formDoctorId] || `User #${formDoctorId}`,
      },
    ]);
  }, [doctorOptions, formDoctorId, doctorLabelById]);

  /** Add/Edit modal — value object for stage Autocomplete (searchable stage list). */
  const modalStageValue = useMemo(() => {
    if (!formStageStatusId) return null;
    const id = Number(formStageStatusId);
    if (!Number.isFinite(id)) return null;
    return stageOptions.find((o) => o.id === id) ?? null;
  }, [formStageStatusId, stageOptions]);

  const stageFilterValue = useMemo(() => {
    if (!filterStageStatusId) return null;
    const id = Number(filterStageStatusId);
    if (!Number.isFinite(id)) return null;
    return stageOptions.find((o) => o.id === id) ?? null;
  }, [filterStageStatusId, stageOptions]);

  /** Standalone: keep doctor filter aligned with ?doctorId= when the URL changes */
  useEffect(() => {
    if (isEmbedded) return;
    setFilterUserId(
      doctorIdFromUrl != null && String(doctorIdFromUrl).trim() !== ""
        ? String(doctorIdFromUrl)
        : "",
    );
  }, [isEmbedded, doctorIdFromUrl]);

  const listFilterSigRef = useRef("");
  const lastCcStageTimeFetchKeyRef = useRef(null);

  const loadRows = useCallback(async () => {
    if (isEmbedded && effectiveDoctorId == null) {
      setRows([]);
      setTotalPages(1);
      return;
    }
    const filterSig = `${isEmbedded}|${effectiveDoctorId ?? ""}|${filterUserId}|${filterStageStatusId}`;
    let pageToFetch = listPage;
    if (listFilterSigRef.current !== filterSig) {
      listFilterSigRef.current = filterSig;
      pageToFetch = 1;
      if (listPage !== 1) {
        setListPage(1);
      }
    }

    const fetchKey = `${filterSig}|p${pageToFetch}`;
    if (lastCcStageTimeFetchKeyRef.current === fetchKey) {
      return;
    }
    lastCcStageTimeFetchKeyRef.current = fetchKey;

    setLoading(true);
    try {
      const userIdForApi = isEmbedded
        ? effectiveDoctorId != null && String(effectiveDoctorId).trim() !== ""
          ? effectiveDoctorId
          : undefined
        : filterUserId != null && String(filterUserId).trim() !== ""
          ? filterUserId
          : undefined;
      const stageStatusIdForApi =
        filterStageStatusId != null && String(filterStageStatusId).trim() !== ""
          ? filterStageStatusId
          : undefined;

      const res = await ReportServices.getCcStageTimeBeta({
        userId: userIdForApi,
        stageStatusId: stageStatusIdForApi,
        page: pageToFetch,
        limit: CC_STAGE_TIME_PAGE_SIZE,
        // roleName: ["CCCoordinator"], // ✅ required for DUT page
      });

      const body =
        res?.data != null && typeof res.data === "object" ? res.data : {};
      let data = [];
      let tp = 1;

      if (Array.isArray(body.data)) {
        data = body.data;
        const t = Number(body.totalPages);
        tp = Number.isFinite(t) && t >= 1 ? t : 1;
      } else if (Array.isArray(res?.data)) {
        data = res.data;
        tp = 1;
      } else if (Array.isArray(body)) {
        data = body;
        tp = 1;
      }

      setRows(data);
      setTotalPages(tp);
      if (pageToFetch > tp) {
        setListPage(Math.max(1, tp));
      }
    } catch (e) {
      lastCcStageTimeFetchKeyRef.current = null;
      handleApiErrorResponses(e);
      setRows([]);
      setTotalPages(1);
    } finally {
      setLoading(false);
    }
  }, [
    isEmbedded,
    effectiveDoctorId,
    filterUserId,
    filterStageStatusId,
    listPage,
  ]);

  useEffect(() => {
    loadStages();
  }, [loadStages]);

  useEffect(() => {
    loadDoctors("");
  }, [loadDoctors]);

  useEffect(() => {
    return () => {
      if (doctorSearchDebounceRef.current) {
        clearTimeout(doctorSearchDebounceRef.current);
      }
      if (modalDoctorSearchDebounceRef.current) {
        clearTimeout(modalDoctorSearchDebounceRef.current);
      }
    };
  }, []);

  /** When Add/Edit modal opens, load full doctor list so the field is searchable from a fresh baseline. */
  useEffect(() => {
    if (!modalOpen) return;
    loadDoctors("");
  }, [modalOpen, loadDoctors]);

  useEffect(() => {
    loadRows();
  }, [loadRows]);

  const openAdd = () => {
    setEditingRow(null);
    const prefillDoctor =
      isEmbedded &&
      effectiveDoctorId != null &&
      String(effectiveDoctorId).trim() !== ""
        ? String(effectiveDoctorId)
        : !isEmbedded &&
            filterUserId != null &&
            String(filterUserId).trim() !== ""
          ? String(filterUserId)
          : "";
    setFormDoctorId(prefillDoctor);
    setFormStageStatusId("");
    setFormSlotStart("");
    setFormSlotEnd("");
    setFormTimeLimit("");
    setModalOpen(true);
  };

  const toggleStatus = async (row) => {
    try {
      const newStatus = row.status === "ACTIVE" ? "INACTIVE" : "ACTIVE";

      await updateCCStageTime({
        ids: [row.id],
        status: newStatus,
      });

      // update UI without refetch
      setRows((prev) =>
        prev.map((r) => (r.id === row.id ? { ...r, status: newStatus } : r)),
      );
    } catch (error) {
      handleApiErrorResponses(error);
    }
  };

  const openEdit = (row) => {
    setEditingRow(row);
    setFormDoctorId(
      row.userId != null && String(row.userId).trim() !== ""
        ? String(row.userId)
        : "",
    );
    const sid = row.stageStatusId;
    setFormStageStatusId(
      sid != null && Number.isFinite(Number(sid)) ? String(sid) : "",
    );
    setFormSlotStart(toIsoDateInput(row.slotStartDate));
    setFormSlotEnd(toIsoDateInput(row.slotEndDate));
    setFormTimeLimit(row.timeLimit != null ? String(row.timeLimit) : "");
    setModalOpen(true);
  };

  const closeModal = () => {
    if (modalDoctorSearchDebounceRef.current) {
      clearTimeout(modalDoctorSearchDebounceRef.current);
      modalDoctorSearchDebounceRef.current = null;
    }
    setModalOpen(false);
    setEditingRow(null);
    setFormDoctorId("");
  };

  const selectedStageLabel = () => {
    const id = Number(formStageStatusId);
    const opt = stageOptions.find((o) => o.id === id);
    return opt?.label ?? "";
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!formDoctorId || String(formDoctorId).trim() === "") {
      window.alert("Please select a doctor.");
      return;
    }
    const uidNum = Number(formDoctorId);
    const userIdForApi = Number.isFinite(uidNum)
      ? uidNum
      : String(formDoctorId).trim();
    const stageStatusId = Number(formStageStatusId);
    if (!Number.isFinite(stageStatusId)) {
      window.alert("Please select a stage.");
      return;
    }
    const commandCenterStageStatus = selectedStageLabel();
    if (!commandCenterStageStatus) {
      window.alert("Invalid stage selection.");
      return;
    }
    if (!formSlotStart || !formSlotEnd) {
      window.alert("Please select start and end dates.");
      return;
    }
    const timeLimitRaw = String(formTimeLimit ?? "").trim();
    if (timeLimitRaw === "") {
      window.alert("Please enter a time limit.");
      return;
    }
    const timeLimitNum = Number(timeLimitRaw);
    if (!Number.isFinite(timeLimitNum) || timeLimitNum <= 0) {
      window.alert("Please enter a valid positive number for time limit.");
      return;
    }
    const body = [
      {
        ...(editingRow?.id != null ? { id: editingRow.id } : {}),
        userId: userIdForApi,
        stageStatusId,
        commandCenterStageStatus,
        slotStartDate: formSlotStart,
        slotEndDate: formSlotEnd,
        timeLimit: timeLimitRaw,
      },
    ];
    setSaving(true);
    try {
      await ReportServices.manageCcUserStageTime(body);
      lastCcStageTimeFetchKeyRef.current = null;
      loadRows();
      closeModal();

      //will reach here
    } catch (err) {
      handleApiErrorResponses(err);
    } finally {
      setSaving(false);
    }
  };

  if (isEmbedded && effectiveDoctorId == null) {
    return (
      <div className="w-full rounded-2xl border border-[#E7E7E7] bg-white p-8 text-center font-lufga text-sm text-[#797979] shadow-sm">
        Select a doctor from the dropdown above to view or manage stage time
        slots.
      </div>
    );
  }

  const filterLabelClass =
    "text-xs font-semibold uppercase tracking-wide text-[#797979] mb-1.5 block font-lufga";

  return (
    <div className="w-full max-w-full font-lufga">
      <div className="flex flex-col gap-4 mb-6">
        <span className="path_line font-lufga w-full text-sm font-semibold text-[#797979] flex items-center flex-wrap">
          <WidgetsOutlinedIcon style={{ height: "24px" }} aria-hidden />
          &nbsp;/&nbsp; Dashboard &nbsp;/&nbsp; DUT case stage update
        </span>
        <div className="h-px w-full bg-[#E7E7E7]" aria-hidden />
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <h1 className="text-[22px] sm:text-2xl font-semibold text-[#191919] font-lufga leading-snug">
            DUT stage time
          </h1>
          <button
            type="button"
            onClick={openAdd}
            className="inline-flex items-center justify-center shrink-0 rounded-full px-6 py-2.5 text-sm font-semibold  border-none text-white bg-[#017C71] hover:bg-[#016a60] transition-colors shadow-sm"
          >
            Add
          </button>
        </div>
      </div>

      <div className="rounded-2xl border border-[#E7E7E7] bg-white p-4 md:p-5 lg:p-6 mb-6 shadow-sm">
        <p className="text-xs font-semibold uppercase tracking-wide text-[#797979] mb-4 font-lufga">
          Filters
        </p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 lg:gap-6 md:items-start">
          {!isEmbedded && (
            <div className="min-w-0">
              <label className={filterLabelClass} htmlFor="cc-filter-doctor">
                Filter by DUT Users
              </label>
              <Autocomplete
                id="cc-filter-doctor"
                size="small"
                fullWidth
                options={doctorAutocompleteOptions}
                value={doctorFilterValue}
                onChange={(_, newValue) => {
                  setFilterUserId(newValue?.id ?? "");
                  if (!newValue) {
                    removeDoctorIdFromUrl();
                    loadDoctors("");
                  }
                }}
                onInputChange={handleDoctorFilterInputChange}
                getOptionLabel={(o) => o.label}
                isOptionEqualToValue={(a, b) => a?.id === b?.id}
                filterOptions={(opts) => opts}
                disableClearable={false}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    placeholder="Search DUT Users…"
                    className="font-lufga"
                    sx={FILTER_FIELD_SX}
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (
                        <>
                          {doctorsLoading ? (
                            <CircularProgress color="inherit" size={16} />
                          ) : null}
                          {params.InputProps.endAdornment}
                        </>
                      ),
                    }}
                  />
                )}
              />
            </div>
          )}
          <div
            className={
              isEmbedded ? "min-w-0 md:col-span-2 lg:max-w-xl" : "min-w-0"
            }
          >
            <label className={filterLabelClass} htmlFor="cc-filter-stage">
              Filter by stage
            </label>
            <Autocomplete
              id="cc-filter-stage"
              size="small"
              fullWidth
              options={stageOptions}
              value={stageFilterValue}
              onChange={(_, newValue) => {
                setFilterStageStatusId(
                  newValue != null ? String(newValue.id) : "",
                );
              }}
              getOptionLabel={(o) => o.label}
              isOptionEqualToValue={(a, b) => a?.id === b?.id}
              filterOptions={filterStageOptions}
              disableClearable={!stageFilterValue}
              renderInput={(params) => (
                <TextField
                  {...params}
                  placeholder="Search or select stage…"
                  className="font-lufga"
                  sx={FILTER_FIELD_SX}
                />
              )}
            />
          </div>
        </div>
      </div>

      <div className="rounded-2xl border border-[#E7E7E7] overflow-hidden bg-white shadow-sm">
        {loading ? (
          <div className="flex justify-center py-16">
            <CircularProgress />
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full min-w-[760px] text-left text-sm">
              <thead>
                <tr className="bg-[#F9F9F9] border-b border-[#E7E7E7]">
                  <th className="px-4 py-3.5 font-semibold text-[#191919] text-sm">
                    CC username name
                  </th>
                  <th className="px-4 py-3.5 font-semibold text-[#191919] text-sm">
                    Stage
                  </th>
                  <th className="px-4 py-3.5 font-semibold text-[#191919] text-sm">
                    Slot start
                  </th>
                  <th className="px-4 py-3.5 font-semibold text-[#191919] text-sm">
                    Slot end
                  </th>
                  <th className="px-4 py-3.5 font-semibold text-[#191919] text-sm">
                    Time limit
                  </th>
                  <th className="px-4 py-3.5 font-semibold text-[#191919] text-sm w-[100px] text-right sm:text-left">
                    Edit
                  </th>
                  <th className="px-4 py-3.5 font-semibold text-[#191919] text-sm">
                    Status
                  </th>
                </tr>
              </thead>
              <tbody>
                {rows.length === 0 ? (
                  <tr>
                    <td
                      colSpan={6}
                      className="px-4 py-10 text-center text-gray-500"
                    >
                      No stage time rows match the current filters. Click Add to
                      create one.
                    </td>
                  </tr>
                ) : (
                  rows.map((row) => (
                    <tr
                      key={
                        row.id ??
                        `${row.userId}-${row.stageStatusId}-${row.slotStartDate}`
                      }
                      className="border-b border-[#F0F0F0] hover:bg-[#FAFAFA]"
                    >
                      <td className="px-4 py-3 text-gray-900">
                        {rowDoctorDisplayName(row, tableDoctorNameByUserId)}
                      </td>
                      <td className="px-4 py-3 text-gray-900">
                        {row.commandCenterStageStatus != null &&
                        String(row.commandCenterStageStatus).trim() !== ""
                          ? String(row.commandCenterStageStatus)
                          : `Stage #${row.stageStatusId ?? "—"}`}
                      </td>
                      <td className="px-4 py-3 text-gray-700">
                        {row.slotStartDate
                          ? toIsoDateInput(row.slotStartDate)
                          : "—"}
                      </td>
                      <td className="px-4 py-3 text-gray-700">
                        {row.slotEndDate
                          ? toIsoDateInput(row.slotEndDate)
                          : "—"}
                      </td>
                      <td className="px-4 py-3 text-gray-700">
                        {row.timeLimit != null ? String(row.timeLimit) : "—"}
                      </td>
                      <td className="px-4 py-3 text-right sm:text-left">
                        <button
                          type="button"
                          onClick={() => openEdit(row)}
                          className="inline-flex items-center gap-1.5 text-[#017C71] hover:underline p-0 m-0 border-0 bg-transparent shadow-none cursor-pointer font-lufga text-sm font-medium focus:outline-none focus-visible:ring-2 focus-visible:ring-[#017C71]/35 focus-visible:ring-offset-2 rounded"
                        >
                          <img
                            src={EditIcon}
                            alt=""
                            className="w-4 h-4 shrink-0"
                            style={{ border: "none", outline: "none" }}
                          />
                          Edit
                        </button>
                      </td>
                      <td className="px-4 py-3">
                        {/* <button
                          type="button"
                          onClick={() => toggleStatus(row)}
                          className={`px-3 py-1 rounded-full text-xs font-semibold ${
                            row.status === "ACTIVE"
                              ? "bg-green-100 text-green-700"
                              : "bg-red-100 text-red-700"
                          }`}
                        >
                          {row.status === "ACTIVE" ? "Active" : "Inactive"}
                        </button> */}
                        <Switch
                          checked={row.status === "ACTIVE"}
                          onChange={() =>
                            window.confirm("Do you want to change status?") &&
                            toggleStatus(row)
                          }
                          style={{ color: "#017C71" }}
                        />
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        )}
        {!loading && totalPages > 1 && (
          <div className="flex justify-center p-4 border-t border-[#E7E7E7]">
            <HospitalCustomPaginaion
              page={listPage}
              totalPages={totalPages}
              setPage={setListPage}
            />
          </div>
        )}
      </div>

      {modalOpen && (
        <div
          className="fixed inset-0 z-[1300] flex items-center justify-center p-4 bg-black/40 overflow-y-auto"
          role="dialog"
          aria-modal="true"
          aria-labelledby="cc-stage-time-modal-title"
          onClick={closeModal}
        >
          <div
            className="w-full max-w-md rounded-2xl bg-white shadow-xl p-6 lg:p-8 my-auto"
            onClick={(ev) => ev.stopPropagation()}
          >
            <div className="flex items-start justify-between gap-3 mb-4">
              <h4
                id="cc-stage-time-modal-title"
                className="font-lufga text-lg font-medium text-gray-900 pr-2"
              >
                {editingRow ? "Edit stage time" : "Add stage time"}
              </h4>
              <button
                type="button"
                onClick={closeModal}
                aria-label="Close dialog"
                className="shrink-0 rounded-lg p-1.5 text-gray-500 hover:bg-gray-100 hover:text-gray-800 transition-colors -mr-1 -mt-1"
              >
                <img src={crossIcon} alt="" className="h-5 w-5" />
              </button>
            </div>
            <form onSubmit={submit} className="flex flex-col gap-4">
              <div className="flex flex-col gap-1.5">
                <span
                  className="text-sm text-gray-700"
                  id="cc-modal-doctor-label"
                >
                  DUT Team Users
                </span>
                <Autocomplete
                  size="small"
                  fullWidth
                  options={modalDoctorAutocompleteOptions}
                  value={modalDoctorValue}
                  onChange={(_, newValue) => {
                    setFormDoctorId(newValue?.id ?? "");
                  }}
                  onInputChange={handleModalDoctorInputChange}
                  getOptionLabel={(o) => o?.label ?? ""}
                  isOptionEqualToValue={(a, b) => a?.id === b?.id}
                  filterOptions={(opts) => opts}
                  loading={doctorsLoading}
                  disabled={
                    doctorsLoading &&
                    modalDoctorAutocompleteOptions.length === 0
                  }
                  noOptionsText={
                    doctorsLoading ? "Loading doctors…" : "No doctors found"
                  }
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      required
                      placeholder="Search or select DUT User…"
                      aria-labelledby="cc-modal-doctor-label"
                      className="font-lufga"
                      sx={FILTER_FIELD_SX}
                      InputProps={{
                        ...params.InputProps,
                        endAdornment: (
                          <>
                            {doctorsLoading ? (
                              <CircularProgress color="inherit" size={16} />
                            ) : null}
                            {params.InputProps.endAdornment}
                          </>
                        ),
                      }}
                    />
                  )}
                />
              </div>

              <div className="flex flex-col gap-1.5">
                <span
                  className="text-sm text-gray-700"
                  id="cc-modal-stage-label"
                >
                  Command center stage status
                </span>
                <Autocomplete
                  size="small"
                  fullWidth
                  options={stageOptions}
                  value={modalStageValue}
                  onChange={(_ev, newValue) => {
                    setFormStageStatusId(
                      newValue?.id != null ? String(newValue.id) : "",
                    );
                  }}
                  getOptionLabel={(o) => o?.label ?? ""}
                  isOptionEqualToValue={(a, b) => a?.id === b?.id}
                  filterOptions={filterStageOptions}
                  disableClearable={false}
                  noOptionsText="No stages found"
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      required
                      placeholder="Search or select stage…"
                      aria-labelledby="cc-modal-stage-label"
                      className="font-lufga"
                      sx={FILTER_FIELD_SX}
                    />
                  )}
                />
              </div>

              <label className="flex flex-col gap-1.5">
                <span className="text-sm text-gray-700">Stage status ID</span>
                <input
                  type="text"
                  readOnly
                  value={formStageStatusId}
                  placeholder="—"
                  aria-readonly="true"
                  className="rounded-lg border border-gray-200 bg-gray-50 px-3 py-2 text-sm text-gray-800 outline-none cursor-default"
                />
              </label>

              <label className="flex flex-col gap-1.5">
                <span className="text-sm text-gray-700 flex items-center gap-2">
                  <img src={calenderIcon} alt="" className="w-4 h-4" />
                  Slot start
                </span>
                <input
                  type="date"
                  required
                  value={formSlotStart}
                  onChange={(e) => setFormSlotStart(e.target.value)}
                  className="rounded-lg border border-gray-300 px-3 py-2 text-sm outline-none focus:border-[#017C71]"
                />
              </label>

              <label className="flex flex-col gap-1.5">
                <span className="text-sm text-gray-700 flex items-center gap-2">
                  <img src={calenderIcon} alt="" className="w-4 h-4" />
                  Slot end
                </span>
                <input
                  type="date"
                  required
                  value={formSlotEnd}
                  onChange={(e) => setFormSlotEnd(e.target.value)}
                  className="rounded-lg border border-gray-300 px-3 py-2 text-sm outline-none focus:border-[#017C71]"
                />
              </label>

              <label className="flex flex-col gap-1.5">
                <span className="text-sm text-gray-700">Time limit</span>
                <input
                  type="text"
                  inputMode="numeric"
                  pattern="[0-9]*"
                  autoComplete="off"
                  required
                  value={formTimeLimit}
                  onChange={(e) =>
                    setFormTimeLimit(digitsOnlyTimeLimit(e.target.value))
                  }
                  placeholder="e.g. 7"
                  aria-label="Time limit"
                  className="rounded-lg border border-gray-300 px-3 py-2 text-sm outline-none focus:border-[#017C71]"
                />
              </label>

              <div className="flex justify-end gap-2 mt-2">
                <button
                  type="button"
                  onClick={closeModal}
                  className="rounded-full px-4 py-2 text-sm border border-gray-300 hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={saving}
                  className="rounded-full px-5 py-2 text-sm font-medium text-white bg-[#017C71] hover:bg-[#016a60] disabled:opacity-60"
                >
                  {saving ? "Saving…" : "Save"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
